/**
 * Processes an array of async operations in controlled batches.
 *
 * @param things - Items to process
 * @param asyncOp - Async function to apply to each item
 * @param inParallel - Number of concurrent chunks to process
 * @returns Promise<U[]> - Flattened or nested results of all operations
 */
export function doInBatches<T, U>(
  things: T[],
  asyncOp: (thing: T) => Promise<U>,
  inParallel = 25,
): Promise<U[]> {
  const chunkSize = Math.ceil(things.length / inParallel)

  // Split into chunks
  const chunks = things.reduce<T[][]>((acc, curr, index) => {
    const chunkIndex = Math.floor(index / chunkSize)
    if (!acc[chunkIndex]) acc[chunkIndex] = []
    acc[chunkIndex].push(curr)
    return acc
  }, [])

  // Map each chunk to a reduce chain of asyncOps
  const chunkPromises = chunks.map((chunk) => {
    // When flattening, collect all results in an array
    return chunk.reduce<Promise<U[]>>((accPromise, item) => {
      return accPromise.then(async (results) => [
        ...results,
        await asyncOp(item),
      ])
    }, Promise.resolve([]))
  })
  // Return results based on flatten parameter
  return Promise.all(chunkPromises).then((result) => {
    return result.flat()
  })
}

export async function processInChunks<T, U>(
  array: T[],
  chunkSize: number,
  callback: (a: T[]) => Promise<U>,
) {
  for (let i = 0; i < array.length; i += chunkSize) {
    const chunk = array.slice(i, i + chunkSize)
    await callback(chunk)
  }
  return Promise.resolve()
}
